import { create } from 'zustand';

/**
 * Shared UI state for the funnel detail editor. Owns selection + draft slot
 * bookkeeping so the focused `useFunnelEditor*` hooks don't need to pass these
 * values around.
 *
 * Data (funnel, pages, productInfo) is NOT stored here — it lives in React
 * Query and is derived in `useFunnelEditorData`.
 */
interface FunnelEditorStore {
    // Selection
    selectedPageId: number | null;
    selectedSuggestedIndex: number | null;
    /**
     * After creating a page where the API didn't echo the new page_id, we
     * park the `order` here and auto-select it once the refetch lands.
     */
    pendingSelectOrder: number | null;

    // Draft slot (local-only; never hits the server until published)
    hasDraftPage: boolean;
    hasAutoCreatedInitialDraft: boolean;

    /**
     * How many suggested-page rows the user has dismissed in the nav.
     * Lets users hide placeholder rows they don't want to fill — we shrink
     * the visible minimum row count by this amount.
     */
    dismissedSuggestedCount: number;

    // Actions
    setSelectedPageId: (pageId: number | null) => void;
    setSelectedSuggestedIndex: (index: number | null) => void;
    setPendingSelectOrder: (order: number | null) => void;
    setHasDraftPage: (value: boolean) => void;
    setHasAutoCreatedInitialDraft: (value: boolean) => void;
    selectPage: (pageId: number) => void;
    selectSuggested: (index: number) => void;
    clearDraft: () => void;
    dismissSuggested: () => void;
    reset: () => void;
}

const initialState = {
    selectedPageId: null,
    selectedSuggestedIndex: null,
    pendingSelectOrder: null,
    hasDraftPage: false,
    hasAutoCreatedInitialDraft: false,
    dismissedSuggestedCount: 0,
};

export const useFunnelEditorStore = create<FunnelEditorStore>(set => ({
    ...initialState,

    setSelectedPageId: selectedPageId => set({ selectedPageId }),
    setSelectedSuggestedIndex: selectedSuggestedIndex => set({ selectedSuggestedIndex }),
    setPendingSelectOrder: pendingSelectOrder => set({ pendingSelectOrder }),
    setHasDraftPage: hasDraftPage => set({ hasDraftPage }),
    setHasAutoCreatedInitialDraft: hasAutoCreatedInitialDraft => set({ hasAutoCreatedInitialDraft }),

    selectPage: pageId => set({ selectedPageId: pageId, selectedSuggestedIndex: null }),
    selectSuggested: index => set({ selectedSuggestedIndex: index, selectedPageId: null }),
    clearDraft: () => set({ hasDraftPage: false }),
    dismissSuggested: () =>
        set(state => ({
            dismissedSuggestedCount: state.dismissedSuggestedCount + 1,
            selectedSuggestedIndex: null,
        })),

    reset: () => set(initialState),
}));
